// @ts-ignore
import { jitsiLocalStorage } from '@jitsi/js-utils';
import PersistenceRegistry from '../../../../react/features/base/redux/PersistenceRegistry';
import ReducerRegistry from '../../../../react/features/base/redux/ReducerRegistry';
/**
 * Retrieves the default state for a given store by accessing the registered reducers.
 *
 * @param {string} storeName - The name of the store.
 * @returns {Record<string, any>} The default state for the given store or an empty object if not found.
 */
const getDefaultStateForStore = (storeName) => {
    // Get the reducer for the specified store
    const reducer = ReducerRegistry._elements[storeName];
    // If a reducer is found, use it to get the initial/default state
    if (reducer) {
        return reducer(undefined, { type: '@@INIT' }) || {};
    }
    // Return an empty object if the reducer or initial state is not found
    return {};
};
/**
 * Resets specific values in the persisted state of a specific store only once, if the version changes.
 * It reads the existing store dynamically and ensures all default properties are present, overriding only the
 * properties passed in `keysToReset`.
 *
 * @param {string} storeName - The name of the store to update (e.g., 'wildix/customStorageSettings').
 * @param {Record<string, any>} keysToReset - An object with keys and their new values to reset in the persisted state.
 * @param {string} currentVersion - The current version string (e.g., 'v1', 'v2').
 */
const resetPersistedStateValuesOnVersionChange = (storeName, keysToReset = {}, currentVersion) => {
    // The key used to track the version of the persisted store in localStorage
    const versionKey = `hasResetVersion_${storeName}`;
    // Retrieve the previously stored version of the state from localStorage
    const previousVersion = jitsiLocalStorage.getItem(versionKey);
    // If the version has changed, we proceed to reset the state
    if (previousVersion !== currentVersion) {
        // Get the current persisted state from PersistenceRegistry
        // or initialize it as an empty object if there is no persisted state yet
        const persistedState = PersistenceRegistry.getPersistedState() || {};
        // Extract the specific store's state or use the dynamically retrieved default state if not present
        const currentStoreState = persistedState[storeName] || {};
        const defaultState = getDefaultStateForStore(storeName);
        // Ensure all properties from defaultState are present and override only the passed keys
        const updatedStoreState = {
            ...defaultState,
            ...currentStoreState,
            ...keysToReset // Override with the passed properties
        };
        // Persist the updated store state in localStorage
        jitsiLocalStorage.setItem(storeName, JSON.stringify(updatedStoreState), true);
        // Update the version in localStorage after the reset
        jitsiLocalStorage.setItem(versionKey, currentVersion, true);
        console.log(`Version updated to "${currentVersion}".
        State updated and persisted for store "${storeName}" with reset values:`, keysToReset);
    }
};
// Reset the 'isPreJoinScreenExternal' property in 'wildix/customStorageSettings' store
// Reset will only happen if the version changes from the previous one
resetPersistedStateValuesOnVersionChange('wildix/customStorageSettings', {
    isPreJoinScreenExternal: true,
    isAutoPip: true,
    audioDeviceIDs: {},
    newNoiseSuppressionType: undefined
}, 'v5');
